import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import OrganizationNavbar from "../../components/layout/OrganizationNavbar";
import Card from "../../components/ui/Card";
import {
  getSidebarItems,
  verifyTokenWithServer,
  getBackendUrl,
  getAuthHeaders,
} from "../../utils/auth";
import TimezoneSelector from "../../components/ui/TimezoneSelector";

export default function OrganizationSettings() {
  const role =
    typeof window !== "undefined" ? localStorage.getItem("authRole") : null;
  const plan =
    typeof window !== "undefined" ? localStorage.getItem("authPlan") : null;
  const sidebarItems = getSidebarItems(role, plan);

  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = await verifyTokenWithServer();
        setUser(userData);
        if (userData && userData.organization_id) {
          const orgRes = await fetch(
            `${getBackendUrl()}/api/organizations/${userData.organization_id}`,
            {
              headers: getAuthHeaders(),
            }
          );
          if (orgRes.ok) {
            const orgData = await orgRes.json();
            setOrganization(orgData);
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!organization) return;

    setSaving(true);
    try {
      const payload = {
        company_size: organization.company_size,
        industry: organization.industry,
        mission: organization.mission,
        vision: organization.vision,
        social_media_links: organization.social_media_links,
      };
      const res = await fetch(
        `${getBackendUrl()}/api/organizations/${organization.id}/profile`,
        {
          method: "PUT",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        const updatedOrg = await res.json();
        setOrganization(updatedOrg);
        alert("Profile updated successfully!");
      } else {
        alert("Failed to update profile");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      alert("Error updating profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      NavbarComponent={OrganizationNavbar}
      sidebarItems={sidebarItems}
    >
      <div className="mb-6">
        <p className="text-gray-600">
          Manage your organization's account, billing, and team settings.
        </p>
      </div>

      <div className="space-y-6">
        {/* Organization Profile */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">
            Organization Profile
          </h3>
          {loading ? (
            <p>Loading...</p>
          ) : organization ? (
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Company Size
                  </label>
                  <input
                    type="text"
                    value={organization.company_size || ""}
                    onChange={(e) =>
                      setOrganization({
                        ...organization,
                        company_size: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Industry
                  </label>
                  <input
                    type="text"
                    value={organization.industry || ""}
                    onChange={(e) =>
                      setOrganization({
                        ...organization,
                        industry: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Mission
                </label>
                <textarea
                  value={organization.mission || ""}
                  onChange={(e) =>
                    setOrganization({
                      ...organization,
                      mission: e.target.value,
                    })
                  }
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Vision
                </label>
                <textarea
                  value={organization.vision || ""}
                  onChange={(e) =>
                    setOrganization({ ...organization, vision: e.target.value })
                  }
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Social Media Links (JSON array)
                </label>
                <textarea
                  value={JSON.stringify(
                    organization.social_media_links || [],
                    null,
                    2
                  )}
                  onChange={(e) => {
                    try {
                      const links = JSON.parse(e.target.value);
                      setOrganization({
                        ...organization,
                        social_media_links: links,
                      });
                    } catch (err) {
                      // invalid JSON, ignore
                    }
                  }}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                  placeholder='[{"platform": "LinkedIn", "url": "https://linkedin.com/company/..."}]'
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Profile"}
              </button>
            </form>
          ) : (
            <p>No organization data available.</p>
          )}
        </Card>

        {/* Current Plan */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Current Plan</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-medium text-gray-900 capitalize flex items-center gap-2">
                {organization?.subscription_status?.is_paid_active
                  ? "Pro"
                  : organization?.subscription_status?.is_trial_active
                  ? "Trial"
                  : "Free"}{" "}
                Plan
                {organization?.subscription_status?.is_paid_active && (
                  <span className="bg-gradient-to-r from-yellow-400 to-green-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                    ACTIVE
                  </span>
                )}
              </p>
              <p className="text-gray-600 text-sm">
                {organization?.subscription_status?.is_paid_active
                  ? "Full access to advanced hiring tools and analytics"
                  : organization?.subscription_status?.is_trial_active
                  ? `Trial active - ${
                      organization?.subscription_status?.features_accessible?.includes(
                        "all"
                      )
                        ? "Full features"
                        : "Limited features"
                    }`
                  : "Limited features for small teams"}
              </p>
              {organization?.subscription_status?.status === "trial" &&
                organization?.subscription_status?.is_trial_active && (
                  <p className="text-xs text-orange-600 mt-1">
                    Trial expires:{" "}
                    {organization?.subscription_status?.trial_start_date
                      ? new Date(
                          new Date(
                            organization.subscription_status.trial_start_date
                          ).getTime() +
                            7 * 24 * 60 * 60 * 1000
                        ).toLocaleDateString()
                      : "Unknown"}
                  </p>
                )}
            </div>
            {!organization?.subscription_status?.is_paid_active && (
              <button className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-green-500 text-white rounded hover:from-yellow-500 hover:to-green-600 font-medium transition-all duration-200">
                Upgrade to Pro
              </button>
            )}
          </div>
        </Card>

        {/* Plan Comparison */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Available Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`border rounded-lg p-4 ${
                !organization?.subscription_status?.is_paid_active
                  ? "border-yellow-400 bg-yellow-50 ring-2 ring-yellow-200"
                  : "border-gray-200"
              }`}
            >
              <h4 className="font-medium text-gray-900">Trial Plan</h4>
              <p className="text-2xl font-bold text-gray-900 mt-2">Free</p>
              <ul className="text-sm text-gray-600 mt-3 space-y-1">
                <li>• Up to 5 team members</li>
                <li>• Basic job posting</li>
                <li>• Candidate tracking</li>
                <li>• Limited analytics</li>
                <li>• 5 interview credits</li>
              </ul>
              {!organization?.subscription_status?.is_paid_active && (
                <span className="inline-block mt-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-medium">
                  Current Plan
                </span>
              )}
            </div>

            <div
              className={`border rounded-lg p-4 ${
                organization?.subscription_status?.is_paid_active
                  ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                  : "border-gray-200"
              }`}
            >
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                Pro Plan
                {organization?.subscription_status?.is_paid_active && (
                  <span className="bg-gradient-to-r from-yellow-400 to-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    ACTIVE
                  </span>
                )}
              </h4>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                $29.99<span className="text-sm font-normal">/month</span>
              </p>
              <ul className="text-sm text-gray-600 mt-3 space-y-1">
                <li>• Unlimited team members</li>
                <li>• Advanced job posting</li>
                <li>• AI-powered candidate matching</li>
                <li>• Full analytics & reporting</li>
                <li>• Integrations & API access</li>
                <li>• Unlimited interviews</li>
                <li>• Priority support</li>
              </ul>
              {organization?.subscription_status?.is_paid_active ? (
                <span className="inline-block mt-3 px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-medium">
                  Current Plan
                </span>
              ) : (
                <button className="mt-3 px-4 py-2 bg-gradient-to-r from-yellow-400 to-green-500 text-white text-sm rounded hover:from-yellow-500 hover:to-green-600 font-medium transition-all duration-200">
                  Upgrade Now
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* Team Management */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Team Management</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-medium text-gray-900">John Smith</p>
                <p className="text-sm text-gray-600">
                  Admin • john@company.com
                </p>
              </div>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                Admin
              </span>
            </div>
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-medium text-gray-900">Sarah Johnson</p>
                <p className="text-sm text-gray-600">
                  Hiring Manager • sarah@company.com
                </p>
              </div>
              <div className="flex space-x-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  Manager
                </span>
                <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                  Edit
                </button>
              </div>
            </div>
            <button className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              + Invite Team Member
            </button>
          </div>
        </Card>

        {/* Payment Methods */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Payment Methods</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                  <span className="text-blue-600 text-sm">💳</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    •••• •••• •••• 4242
                  </p>
                  <p className="text-sm text-gray-600">Expires 12/25</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                  Edit
                </button>
                <button className="px-3 py-1 border border-red-300 text-red-600 rounded text-sm hover:bg-red-50">
                  Remove
                </button>
              </div>
            </div>
            <button className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              + Add Payment Method
            </button>
          </div>
        </Card>

        {/* Billing History */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Billing History</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-medium text-gray-900">Pro Plan - Monthly</p>
                <p className="text-sm text-gray-600">November 1, 2024</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">$29.99</p>
                <button className="text-sm text-blue-600 hover:text-blue-800">
                  Download
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Organization Preferences */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">
            Organization Preferences
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Email Notifications</p>
                <p className="text-sm text-gray-600">
                  Receive updates about team activity and billing
                </p>
              </div>
              <input type="checkbox" defaultChecked className="rounded" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Team Invitations</p>
                <p className="text-sm text-gray-600">
                  Allow team members to invite others
                </p>
              </div>
              <input type="checkbox" defaultChecked className="rounded" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Data Analytics</p>
                <p className="text-sm text-gray-600">
                  Help improve our service with usage analytics
                </p>
              </div>
              <input type="checkbox" defaultChecked className="rounded" />
            </div>
          </div>
        </Card>

        {/* Timezone Settings */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">
            Timezone Settings
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Set your organization's timezone to ensure interview times are
            displayed correctly for all team members.
          </p>
          <TimezoneSelector
            organizationId={organization?.id}
            showCurrentTime={true}
          />
        </Card>

        {/* Danger Zone */}
        <Card>
          <h3 className="font-semibold text-red-800 mb-4">Danger Zone</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-red-200 rounded">
              <div>
                <p className="font-medium text-gray-900">Cancel Subscription</p>
                <p className="text-sm text-gray-600">
                  End your subscription and downgrade to trial plan
                </p>
              </div>
              <button className="px-4 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50">
                Cancel
              </button>
            </div>
            <div className="flex items-center justify-between p-3 border border-red-200 rounded">
              <div>
                <p className="font-medium text-gray-900">Delete Organization</p>
                <p className="text-sm text-gray-600">
                  Permanently delete the organization and all associated data
                </p>
              </div>
              <button className="px-4 py-2 border border-red-300 text-red-600 rounded hover:bg-red-50">
                Delete
              </button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
